import { Editor } from '@tiptap/core';
import { Body, Heading } from 'copilot-design-system';
import { FormikProps } from 'formik';
import React, { useContext, useMemo } from 'react';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { AlphaNumericRegex } from 'src/constants';
import { RouteContext } from 'src/context';
import { FormModal } from 'src/legacy/components/Modals';
import { BaseTextField } from 'src/legacy/components/TextField';
import { BillingRichTextEditor } from 'src/routes/billing/components/BillingRichTextEditor';
import {
  useGetProductByIdQuery,
  useUpdateProductMutation,
} from 'src/services/api/productsApi';
import { getPublicImageURL } from 'src/utils/ImageUtils';

import { ProductFileDropzone } from '../ProductEditPage/ProductFileDropzone';

interface Props {
  open: boolean;
  onCloseModal: () => void;
}
export const productDetailSchema = z.object({
  id: z.string(),
  name: z
    .string({ required_error: 'Name required' })
    .refine(
      (val: string) => AlphaNumericRegex.test(val.replaceAll(' ', '')),
      'Name cannot be only numbers',
    ),
  description: z.string().optional(),
  imageUrls: z.array(z.string()),
});

export type ProductDetailsSchema = z.infer<typeof productDetailSchema>;

const useForm = () => {
  const FormRenderer = ({
    touched,
    errors,
    handleBlur,
    handleChange,
    values,
    setFieldValue,
  }: FormikProps<ProductDetailsSchema>) => {
    const handleContentChange = (editor: Editor) => {
      setFieldValue('description', editor.getHTML());
    };

    return (
      <div className="flex flex-col gap-4">
        <BaseTextField
          label="Name"
          fullWidth
          sizeVariant="medium"
          name="name"
          id="input-lineitem-name"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.name || ''}
          variant="outlined"
          helperText={
            !errors.name ? (
              <Body className="text-[13px] text-gray-small">
                For optimal search visibility, keep titles less than 60
                characters.
              </Body>
            ) : (
              touched.name && errors.name
            )
          }
          error={Boolean(touched.name && errors.name)}
        />

        <div>
          <div className="flex items-center mb-1">
            <Heading className="text-black-heading">Description</Heading>
            <Heading className=" text-gray-small">&nbsp;- Optional</Heading>
          </div>
          <div>
            <BillingRichTextEditor
              description={values.description || ''}
              onContentChange={handleContentChange}
            />
          </div>
          <Body className="text-[13px] text-gray-small text-right">
            For optimal search visibility, keep descriptions less than 155
            characters.
          </Body>
        </div>

        <div>
          <div className="flex items-center mb-1">
            <Heading className="text-black-heading">Image</Heading>
          </div>
          <ProductFileDropzone
            imageUrl={getPublicImageURL(String(values.imageUrls || ''), {
              resize: { width: 300, height: 300 },
            })}
          />
          <div className="flex justify-end">
            <div className="flex justify-end">
              <>
                <Body className=" text-[13px] text-gray-small text-right">
                  {'Recommended at least 448x448 pixels.'}
                </Body>
              </>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return {
    FormRenderer,
    validationScheme: toFormikValidationSchema(productDetailSchema),
  };
};

export const EditProductDetail: React.FC<Props> = ({ open, onCloseModal }) => {
  const { pathname } = useContext(RouteContext);
  const productId = pathname.split('/').pop();
  const { data: product } = useGetProductByIdQuery(productId!, {
    skip: !productId,
  });
  const [updateProduct] = useUpdateProductMutation();

  const initialFormValue: ProductDetailsSchema = useMemo(
    () => ({
      id: product?.id ?? '',
      name: product?.name ?? '',
      description: product?.description ?? '',
      imageUrls: product?.imageUrls ?? [],
    }),
    [product],
  );

  const handleCloseModal = () => {
    onCloseModal();
  };

  const handleUpdateProduct = async (values: ProductDetailsSchema) => {
    if (!productId) return;
    await updateProduct({
      payload: values,
    });
  };

  return (
    <FormModal
      title={'Edit details'}
      buttonLabel={'Update'}
      initialFormValue={initialFormValue}
      useFormHook={useForm}
      handleSave={handleUpdateProduct}
      open={open}
      onClose={handleCloseModal}
    />
  );
};
